import React, { useEffect } from 'react';
import { useLocation, useDispatch } from 'umi';
import { 
  WeeklyProjectHighlightDocPreviewer,
  PortfolioOverviewDocPreviewer,
  StudiosDocPreviewer,
  BoardGovernanceDocPreviewer,
  InDevProjectsDocPreviewer,
  PublishingReviewsDocPreviewer,
  LiveTitlesDocPreviewer,
} from './components';

import type { IRecord } from '@gp/types/global';

// previewers映射
const PREVIEWERS: Record<string, React.FC<any>> = {
  weekly_project_highlight: WeeklyProjectHighlightDocPreviewer,
  'mgmt/studio/portfolio_overview/notes': PortfolioOverviewDocPreviewer, // Reuse PortfolioOverviewDocPreviewer for Notes section
  'mgmt/studio/board_governance': BoardGovernanceDocPreviewer,
  'mgmt/studio/studios/notes': StudiosDocPreviewer, // Reuse StudiosDocPreviewer for Notes section
  'mgmt/studio/in_dev/gr_management': InDevProjectsDocPreviewer,
  'mgmt/studio/in_dev/notes': InDevProjectsDocPreviewer, // Reuse InDevProjectsDocPreviewer for Notes section
  'mgmt/publishing/project/publishing_reviews': PublishingReviewsDocPreviewer,
  'mgmt/studio/live_titles/notes': LiveTitlesDocPreviewer,
};

/**
 * 解析查询参数
 * @param search 查询参数字符串
 * @returns 查询参数对象
 */
const analysisSearchParams = (
  search: string,
): {
  previewer_type: 'weekly_project_highlight' | 'mgmt/studio/portfolio_overview/notes' | 'mgmt/studio/studios/notes' | 'mgmt/studio/board_governance' | 'mgmt/studio/in_dev/gr_management' | 'mgmt/studio/in_dev/notes' | 'mgmt/publishing/project/publishing_reviews' | 'mgmt/studio/live_titles/notes' | (string & {});
  [key: string]: any;
} => {
  const searchParams = new URLSearchParams(search);
  const others: IRecord<any> = {};

  searchParams.forEach((value, key) => {
    if (key === 'previewer_type') return;
    others[key] = value;
  });

  return { ...others, previewer_type: searchParams.get('previewer_type') || '' };
};

// previewer识别失败时的兜底展示
const UnknownPreviewer: React.FC = () => <div style={{ margin: 20, textAlign: 'center' }}>Unknown Previewer</div>;

const StrategicCOSPreviewers: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { previewer_type: previewerType, ...restParams } = analysisSearchParams(location.search);
  const Previewer = PREVIEWERS[previewerType] || UnknownPreviewer;
  const [isUserInfoLoaded, setIsUserInfoLoaded] = React.useState(false);

  // Initialize user info since this is an independent route (bypasses layout)
  useEffect(() => {
    // Check if userName already exists (from previous navigation)
    const existingUserName = sessionStorage.getItem('userName');
    if (existingUserName) {
      setIsUserInfoLoaded(true);
      return;
    }
    
    dispatch({
      type: 'home/getUserInfo'
    });
    dispatch({
      type: 'home/getNavMenuList'
    });
    dispatch({
      type: 'home/getNavStudioList'
    });
    
    // Wait for getUserInfo to complete (check every 100ms for max 3 seconds)
    let attempts = 0;
    const maxAttempts = 30;
    const checkInterval = setInterval(() => {
      attempts++;
      const userName = sessionStorage.getItem('userName');
      
      if (userName) {
        setIsUserInfoLoaded(true);
        clearInterval(checkInterval);
      } else if (attempts >= maxAttempts) {
        setIsUserInfoLoaded(true); // Allow page to load anyway
        clearInterval(checkInterval);
      }
    }, 100);
    
    return () => clearInterval(checkInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  console.log('StrategicCOSPreviewers:', {
    search: location.search,
    previewerType,
    restParams,
    hasPreview: !!PREVIEWERS[previewerType],
    isUserInfoLoaded,
  });

  // Wait for user info to load before rendering previewer
  if (!isUserInfoLoaded) {
    return (
      <div className="dbs-strategic-cos-previewers" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="dbs-strategic-cos-previewers">
      <Previewer {...restParams} />
    </div>
  );
};

export default StrategicCOSPreviewers;
